<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Current Balance Card -->
    <v-card outlined class="mb-4 ordera-box-shadow" v-if="selectedUsers.length === 1">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6">
            <div class="text-h6 grey--text mb-2">{{ $t("current_balance") }}</div>
            <div class="text-h4 primary--text font-weight-bold">
              {{ formatCurrency(currentBalance) }}
            </div>
          </v-col>
          <v-col cols="12" sm="6" class="text-sm-right">
            <v-icon size="64" color="primary" class="opacity-20">mdi-wallet</v-icon>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Top-up Form Card -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card outlined class="ordera-box-shadow">
          <v-card-title>
            <v-icon left color="primary">mdi-cash-plus</v-icon>
            {{ $t("add_credit") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form" v-model="valid" @submit.prevent="submitTopup">
              <v-row>
                <!-- Users Selection -->
                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="selectedUsers"
                    :items="users"
                    :label="$t('users')"
                    item-text="name"
                    item-value="id"
                    :rules="[(v) => (v && v.length > 0) || $t('required_field')]"
                    prepend-inner-icon="mdi-account-multiple"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="toppedBy"
                    :items="toppedByOptions"
                    :label="$t('topped_by')"
                    item-text="label"
                    item-value="value"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-account-check"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Amount Input -->
                <v-col cols="12" md="6">
                  <text-field
                    v-model="topupAmount"
                    :label="$t('amount')"
                    type="number"
                    :rules="amountRules"
                    prepend-inner-icon="mdi-cash"
                    suffix="KM"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Payment Method -->
                <v-col cols="12" md="6">
                  <autocomplete
                    v-model="paymentMethod"
                    :items="paymentMethods"
                    :label="$t('payment_method')"
                    item-text="label"
                    item-value="value"
                    :rules="[(v) => !!v || $t('required_field')]"
                    prepend-inner-icon="mdi-credit-card"
                    outlined
                    dense
                  />
                </v-col>

                <!-- Quick Amount Buttons -->
                <v-col cols="12">
                  <div class="text-caption font-weight-medium">
                    {{ $t("quick_amounts") }}
                  </div>
                  <v-chip-group v-model="selectedQuickAmount" mandatory>
                    <v-chip
                      v-for="amount in quickAmounts"
                      :key="amount"
                      :value="amount"
                      color="primary"
                      @click="setQuickAmount(amount)"
                    >
                      {{ amount }} KM
                    </v-chip>
                  </v-chip-group>
                </v-col>

                <!-- Submit Button -->
                <v-col cols="12" v-if="valid">
                  <primary-button
                    @click="submitTopup"
                    :disabled="submitting"
                    :loading="submitting"
                    block
                    color="success"
                  >
                    <v-icon left>mdi-plus-circle</v-icon>
                    {{ $t("add_credit") }}
                  </primary-button>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Recent Top-ups -->
      <v-col cols="12" md="6">
        <v-card outlined class="ordera-box-shadow" v-if="filteredRecentTopups.length > 0">
          <v-card-title>
            <v-icon left color="grey">mdi-history</v-icon>
            {{ $t("recent_topups") }}
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="topupHeaders"
            :items="filteredRecentTopups"
            :items-per-page="5"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.num="{ index }">
              {{ index + 1 }}
            </template>
            <template v-slot:item.user_name="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ item.user_name }}
                  </span>
                </template>
                <span>{{ item.id }}</span>
              </v-tooltip>
            </template>
            <template v-slot:item.date="{ item }">
              {{ localeDate(item.date) }}
            </template>

            <template v-slot:item.current_balance="{ item }">
              <span class="font-weight-medium">
                {{ formatCurrency(item.current_balance) }}
              </span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span class="font-weight-bold success--text">
                +{{ formatCurrency(item.amount) }}
              </span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import employeeService from "@/services/employee";

export default {
  name: "CompanyMultiRestaurantTabTopup",
  mixins: [hasSnackbar],
  props: {
    preselectedUserId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      valid: false,
      submitting: false,
      topupAmount: null,
      paymentMethod: null,
      toppedBy: this.getDefaultToppedBy(),
      description: "",
      selectedQuickAmount: null,
      selectedUsers: [],
      quickAmounts: [10, 20, 50, 100, 200],
      currentBalance: 150.0, // TODO: Get from API based on selected user
      recentTopups: [],
      users: [],
    };
  },
  mounted() {
    this.fetchUsers();
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    userRole() {
      return this.$store.state.user?.role || this.$store.state.user?.user_type;
    },
    topupHeaders() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("amount") + " (KM)", value: "amount", align: "right" },
        { text: this.$t("payment_type"), value: "payment_type" },
        { text: this.$t("topped_by"), value: "topped_by" },
        {
          text: this.$t("current_balance") + " (KM)",
          value: "current_balance",
          align: "right",
        },
        { text: this.$t("date"), value: "date" },
      ];
    },
    paymentMethods() {
      return [
        { label: this.$t("cash"), value: "cash" },
        { label: this.$t("card"), value: "card" },
        { label: this.$t("bank_transfer"), value: "transfer" },
      ];
    },
    toppedByOptions() {
      return [
        { label: "Firma", value: "Firma" },
        { label: "Radnik", value: "Radnik" },
      ];
    },
    amountRules() {
      return [
        (v) => !!v || this.$t("required_field"),
        (v) => v > 0 || this.$t("amount_must_be_positive"),
        (v) => v <= 1000 || this.$t("max_amount_exceeded"),
      ];
    },
    filteredRecentTopups() {
      if (this.selectedUsers.length === 0) return [];

      const fakeData = this.selectedUsers
        .map((userId) => this.generateFakeTopup(userId))
        .filter(Boolean);

      return [...fakeData, ...this.recentTopups].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
    },
  },
  watch: {
    selectedUsers(newVal) {
      // If only one user selected, fetch their balance
      if (newVal.length === 1) {
        this.fetchUserBalance(newVal[0]);
      }
    },
    preselectedUserId: {
      handler(newVal) {
        if (newVal && this.users.length > 0) {
          this.selectedUsers = [newVal];
        }
      },
      immediate: false,
    },
  },
  methods: {
    getDefaultToppedBy() {
      return ["company", "Company"].includes(this.userRole) ? "Firma" : "Radnik";
    },
    generateFakeTopup(userId) {
      const user = this.users.find((u) => u.id === userId);
      if (!user) return null;
      const randomDate = new Date(2025, 0, 1 + Math.random() * 375);
      const amounts = [20, 50, 75, 100, 150, 200];
      const paymentTypes = ["Gotovina", "Kartica", "Bankarski prenos"];
      return {
        id: userId * 1000,
        user_name: user.name,
        date: randomDate.toISOString().substr(0, 10),
        current_balance: (Math.random() * 500).toFixed(2),
        amount: amounts[Math.floor(Math.random() * amounts.length)],
        payment_type: paymentTypes[Math.floor(Math.random() * paymentTypes.length)],
        topped_by: ["Firma", "Radnik"][Math.floor(Math.random() * 2)],
        status: "completed",
      };
    },
    async fetchUsers() {
      if (!this.companyId) return;
      try {
        const { data } = await employeeService.getCompanyUsers(this.companyId);
        this.users = (data.users || []).map((u) => ({
          id: u.id,
          name:
            u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username,
        }));
        if (this.preselectedUserId) this.selectedUsers = [this.preselectedUserId];
      } catch (error) {
        console.error("Error fetching users:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_data"));
      }
    },
    setQuickAmount(amount) {
      this.topupAmount = amount;
    },
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    async fetchUserBalance(userId) {
      // TODO: Implement API call to fetch user balance
      // Mock implementation
      this.currentBalance = 150.0;
    },
    async submitTopup() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;
      try {
        // TODO: Implement API call
        // const response = await creditService.addCreditToUsers({
        //   user_ids: this.selectedUsers,
        //   amount: this.topupAmount,
        //   payment_method: this.paymentMethod,
        //   description: this.description
        // });

        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Mock success message
        const userCount = this.selectedUsers.length;
        const totalAmount = parseFloat(this.topupAmount) * userCount;

        this.showSuccessSnackbar(
          `${this.$t("credit_added_successfully")} - ${userCount} ${this.$t(
            "users"
          )}, ${this.formatCurrency(totalAmount)} KM`
        );

        // Add to recent topups for each user
        const paymentMethodLabel =
          this.paymentMethods.find((m) => m.value === this.paymentMethod)?.label ||
          this.paymentMethod;

        this.selectedUsers.forEach((userId) => {
          const user = this.users.find((u) => u.id === userId);
          this.recentTopups.unshift({
            id: Date.now() + userId,
            user_name: user?.name || "Unknown",
            date: new Date().toISOString().substr(0, 10),
            current_balance: (Math.random() * 500).toFixed(2),
            amount: parseFloat(this.topupAmount),
            payment_type: paymentMethodLabel,
            topped_by: this.toppedBy,
            status: "completed",
          });
        });

        // Keep only last 10 topups
        this.recentTopups = this.recentTopups.slice(0, 10);

        this.resetForm();
      } catch (error) {
        console.error("Error adding credit:", error);
        this.showErrorSnackbar(error, this.$t("error_adding_credit"));
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.topupAmount = null;
      this.paymentMethod = null;
      this.toppedBy = null;
      this.description = "";
      this.selectedQuickAmount = null;
      this.selectedUsers = [];
      this.$refs.form?.resetValidation();
    },
  },
};
</script>

<style scoped>
.opacity-20 {
  opacity: 0.2;
}
</style>
